import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';

export const THEME_STORAGE_KEY = 'site-theme-preference';
export const THEME_STORAGE_TTL_MS = 6 * 60 * 60 * 1000;

const VALID_THEME_PREFERENCES = ['system', 'light', 'dark'];

const ThemeContext = createContext(null);

export const normalizeThemePreference = (value) =>
  VALID_THEME_PREFERENCES.includes(value) ? value : 'system';

export const getStoredThemePreference = () => {
  if (typeof window === 'undefined') {
    return 'system';
  }

  try {
    const rawValue = window.localStorage.getItem(THEME_STORAGE_KEY);

    if (!rawValue) {
      return 'system';
    }

    const parsedValue = JSON.parse(rawValue);
    const themePreference = normalizeThemePreference(parsedValue?.value);
    const savedAt = parsedValue?.savedAt;
    const isExpired =
      typeof savedAt !== 'number' || Date.now() - savedAt > THEME_STORAGE_TTL_MS;

    if (themePreference === 'system' || isExpired) {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
      return 'system';
    }

    return themePreference;
  } catch (error) {
    return 'system';
  }
};

export const resolveThemePreference = (themePreference, prefersDarkMode) =>
  themePreference === 'system' ? (prefersDarkMode ? 'dark' : 'light') : themePreference;

export const ThemeProvider = ({ children }) => {
  const prefersDarkMode = useMedia('(prefers-color-scheme: dark)', true);
  const [themePreference, setThemePreference] = useState(getStoredThemePreference);

  const resolvedTheme = resolveThemePreference(themePreference, prefersDarkMode);
  const isDarkMode = resolvedTheme === 'dark';
  const isLightMode = resolvedTheme === 'light';

  useEffect(() => {
    try {
      if (themePreference === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
        return;
      }

      window.localStorage.setItem(
        THEME_STORAGE_KEY,
        JSON.stringify({
          value: themePreference,
          savedAt: Date.now(),
        })
      );
    } catch (error) {
      // Ignore storage failures and keep the theme in memory.
    }
  }, [themePreference]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.setAttribute('data-theme-preference', themePreference);
    document.documentElement.setAttribute('data-bs-theme', resolvedTheme);
  }, [resolvedTheme, themePreference]);

  const value = useMemo(
    () => ({
      themePreference,
      resolvedTheme,
      isDarkMode,
      isLightMode,
      setThemePreference,
    }),
    [themePreference, resolvedTheme, isDarkMode, isLightMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
